import { useCallback, useEffect, useState } from 'react';

import { AccountingProviderLog } from '@/pages/AccountingProvidersPage/components/AccountingProviderLogTable/types';
import Logger from '@/services/logger';
import { AccountingProvidersAPI } from '@/services/networking/accountingProviders';
import { AccountingLogAction } from '@/types/invoicing';

import { useFormatDate } from './useFormatDate';

type Props = {
    onError?: (error: Error) => void;
};

const INFO_LOG_ACTIONS: AccountingLogAction[] = [
    'SYNC_COMPLETE',
    'INTEGRATION_CONFIGURED',
    'INTEGRATION_DISCONNECTED',
];
const ERROR_LOG_ACTIONS: AccountingLogAction[] = ['SYNC_ERROR'];
const WARNING_LOG_ACTIONS: AccountingLogAction[] = [
    'RATE_LIMIT_HIT',
    'TOKEN_REVOKED',
];

const mapLogActionToType = (
    action: AccountingLogAction,
): 'info' | 'error' | 'warning' => {
    switch (true) {
        case INFO_LOG_ACTIONS.includes(action):
            return 'info';
        case ERROR_LOG_ACTIONS.includes(action):
            return 'error';
        case WARNING_LOG_ACTIONS.includes(action):
            return 'warning';
        default:
            return 'info';
    }
};

const getISODate = (date: number) => {
    return new Date(date).toISOString();
};

export function useAccountingProviderLogs({ onError }: Props = {}) {
    const [logs, setLogs] = useState<AccountingProviderLog[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const formatDate = useFormatDate();

    const fetchAccountingLogs = useCallback(async () => {
        try {
            setIsLoading(true);

            const data = await AccountingProvidersAPI.getAccountingLogs();

            setLogs(
                data.map((log) => ({
                    id: log.id,
                    date: formatDate(getISODate(log.created_at), 'logdatetime'),
                    message: log.message,
                    type: mapLogActionToType(log.action),
                    provider: log.provider,
                })),
            );
        } catch (err) {
            Logger.logError(`Failed to fetch provider logs: ${err.message}`);
            onError?.(err);
        } finally {
            setIsLoading(false);
        }
    }, [onError, formatDate]);

    useEffect(() => {
        fetchAccountingLogs();
    }, [fetchAccountingLogs]);

    return {
        logs,
        isLoading,
        refetch: fetchAccountingLogs,
    };
}
